










































































































































import { Component, Mixins, Prop } from 'vue-property-decorator';
import moment, { Moment } from 'moment';

import Modal from '@/components/UI/Modal.vue';
import Toast from '@/mixins/toast';

import MmHintedButton from '../../components/MmHintedButton.vue';
import MmStates from '../../components/MmStates.vue';
import {
	createPhotoExport,
	downloadPhotoExportArchive,
	fetchPhotoExports,
	isPhotoExportTerminal,
	cancelPhotoExport,
} from '../../api/photoExports';
import { MmError, toMmError } from '../../core/errors';
import { MmErrorCode } from '../../types/enums';
import { readMmError } from '../../api/mmHttp';
import {
	MmPhotoExport,
	MmPhotoExportLayout,
	MmPhotoExportManifestFormat,
	MmPhotoExportStatus,
} from '../../types/dto';

const KEY = 'priceMonitoring.mobileMonitoring.photoExports.';
const DATE_WIRE_FORMAT = 'YYYY-MM-DD';
const POLL_MS = 10000;

@Component({
	components: {
		Modal,
		MmHintedButton,
		MmStates,
	},
})
export default class PhotoExportsPanel extends Mixins(Toast) {
	@Prop({ default: null }) readonly tradingId!: number | null;

	@Prop({ default: true }) readonly isActive!: boolean;

	@Prop({ default: null }) readonly tradingName!: string | null;

	visible: boolean = false;

	exports: MmPhotoExport[] = [];

	isListLoading: boolean = false;

	isCreating: boolean = false;

	listError: MmError | null = null;

	createFrom: string = '';

	createTo: string = '';

	createLayout: MmPhotoExportLayout = 'BY_STORE_BY_DATE';

	createManifest: MmPhotoExportManifestFormat = 'BOTH';

	restoreHint: string = '';

	private pollTimer: number | null = null;

	readonly exportKeyPrefix = KEY;

	get disabled(): boolean {
		return this.tradingId === null || this.tradingId <= 0;
	}

	get disabledReason(): string {
		return this.disabled ? (this.$t(KEY + 'networkRequired') as string) : '';
	}

	get canCreate(): boolean {
		return this.createFrom !== '' && this.createTo !== '';
	}

	get createDisabledReason(): string {
		if (!this.canCreate) {
			return this.$t(KEY + 'periodRequired') as string;
		}

		return '';
	}

	get createPeriodRange(): [Moment, Moment] | null {
		if (this.createFrom === '' || this.createTo === '') {
			return null;
		}

		return [moment(this.createFrom, DATE_WIRE_FORMAT), moment(this.createTo, DATE_WIRE_FORMAT)];
	}

	get columns(): Array<{ title: string; dataIndex: string; key: string; scopedSlots?: { customRender: string } }> {
		return [
			{
				title: this.$t(KEY + 'colCreated') as string,
				dataIndex: 'createdAt',
				key: 'createdAt',
			},
			{
				title: this.$t(KEY + 'colStatus') as string,
				dataIndex: 'status',
				key: 'status',
				scopedSlots: { customRender: 'status' },
			},
			{
				title: this.$t(KEY + 'colProgress') as string,
				dataIndex: 'progress',
				key: 'progress',
				scopedSlots: { customRender: 'progress' },
			},
			{
				title: this.$t(KEY + 'colPhotos') as string,
				dataIndex: 'photosTotal',
				key: 'photosTotal',
			},
			{
				title: this.$t(KEY + 'colActions') as string,
				dataIndex: 'exportId',
				key: 'actions',
				scopedSlots: { customRender: 'actions' },
			},
		];
	}

	beforeDestroy(): void {
		this.stopPolling();
	}

	openModal(): void {
		this.visible = true;
		this.resetCreatePeriod();
		this.restoreHint = '';
		this.loadExports();
		this.startPolling();
	}

	closeModal(): void {
		this.visible = false;
		this.stopPolling();
	}

	handleCreatePeriodChange(dates: [Moment, Moment] | null, dateStrings: string[]): void {
		if (!dates || dates.length !== 2) {
			this.createFrom = '';
			this.createTo = '';

			return;
		}

		this.createFrom = dateStrings[0] ? dateStrings[0] : dates[0].format(DATE_WIRE_FORMAT);
		this.createTo = dateStrings[1] ? dateStrings[1] : dates[1].format(DATE_WIRE_FORMAT);
	}

	handleLayoutChange(value: MmPhotoExportLayout): void {
		this.createLayout = value;
	}

	handleManifestChange(value: MmPhotoExportManifestFormat): void {
		this.createManifest = value;
	}

	handleCreate(): void {
		if (!this.canCreate || this.isCreating) {
			return;
		}

		this.isCreating = true;
		this.restoreHint = '';

		const shotFrom = moment(this.createFrom, DATE_WIRE_FORMAT).startOf('day').toISOString();
		const shotTo = moment(this.createTo, DATE_WIRE_FORMAT).endOf('day').toISOString();

		createPhotoExport({
			filters: {
				taskIds: [],
				subTaskIds: [],
				storeIds: [],
				auditorIds: [],
				shotFrom: shotFrom,
				shotTo: shotTo,
				onlyAccepted: false,
			},
			layout: this.createLayout,
			manifestFormat: this.createManifest,
		})
			.then((response) => {
				const item = response.data as MmPhotoExport;

				this.showInfo(this.$t(KEY + 'createSuccess') as string);
				this.applyRestoreHint(item);
				this.loadExports();
			})
			.catch((error) => {
				this.showMmError(error);
			})
			.finally(() => {
				this.isCreating = false;
			});
	}

	loadExports(): void {
		if (this.disabled) {
			return;
		}

		this.isListLoading = true;
		this.listError = null;

		fetchPhotoExports()
			.then((page) => {
				this.exports = page.content;
			})
			.catch((error) => {
				this.listError = toMmError(error, 'photo-exports/list');
				this.showMmError(error);
			})
			.finally(() => {
				this.isListLoading = false;
			});
	}

	handleDownload(record: MmPhotoExport): void {
		downloadPhotoExportArchive(record.exportId, record.fileName)
			.catch((error) => {
				this.showMmError(error);
			});
	}

	handleCancel(record: MmPhotoExport): void {
		cancelPhotoExport(record.exportId)
			.then(() => {
				this.showInfo(this.$t(KEY + 'cancelSuccess') as string);
				this.loadExports();
			})
			.catch((error) => {
				this.showMmError(error);
			});
	}

	statusLabel(status: MmPhotoExportStatus): string {
		const key = KEY + 'status.' + status;

		return this.$te(key) ? (this.$t(key) as string) : status;
	}

	isTerminal(status: MmPhotoExportStatus): boolean {
		return isPhotoExportTerminal(status);
	}

	/** Штатный показ ошибки не годится: при двоичном ответе текста нет и в плашку попадало undefined */
	private showMmError(error: any): void {
		const mmError = readMmError(error, 'photo-exports');

		if (mmError.code === MmErrorCode.CANCELED) {
			return;
		}

		const message = mmError.message !== '' ? mmError.message : (this.$t(mmError.i18nKey) as string);

		this.showError(message);
	}

	private applyRestoreHint(item: MmPhotoExport): void {
		if (!item.requiresRestore) {
			this.restoreHint = '';

			return;
		}

		const hours = item.estimatedReadyHours;

		this.restoreHint =
			hours !== null
				? (this.$t(KEY + 'restoreHintHours', { hours: hours }) as string)
				: (this.$t(KEY + 'restoreHint') as string);
	}

	private resetCreatePeriod(): void {
		const to = moment();
		const from = moment().subtract(29, 'days');

		this.createFrom = from.format(DATE_WIRE_FORMAT);
		this.createTo = to.format(DATE_WIRE_FORMAT);
	}

	private startPolling(): void {
		this.stopPolling();

		this.pollTimer = window.setInterval(() => {
			if (!this.visible) {
				return;
			}

			const hasActive = this.exports.some((row) => !isPhotoExportTerminal(row.status));

			if (hasActive) {
				this.loadExports();
			}
		}, POLL_MS);
	}

	private stopPolling(): void {
		if (this.pollTimer !== null) {
			window.clearInterval(this.pollTimer);
			this.pollTimer = null;
		}
	}
}
